import React, { useState, useEffect, useMemo } from 'react';
import MuiSlider from '@mui/material/Slider';
import PlayCircleOutlineRoundedIcon from '@mui/icons-material/PlayCircleOutlineRounded';
import StopCircleRoundedIcon from '@mui/icons-material/StopCircleRounded';
// import Dialog from './dialog_slider.js';
import 'leaflet/dist/leaflet.css';

function formatDateForGeoServer(date) {
  const utcDate = new Date(Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    0, 0, 0
  ));
  return utcDate.toISOString().replace(/:\d{2}\.\d{3}Z$/, ':00Z'); // Formato ISO sin milisegundos
}

function formatDateForDisplay(date) {
  const year = date.getUTCFullYear();
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
  const day = date.getUTCDate().toString().padStart(2, '0');
  const hours = date.getUTCHours().toString().padStart(2, '0');
  const minutes = date.getUTCMinutes().toString().padStart(2, '0');
  const seconds = date.getUTCSeconds().toString().padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds} UTC`;
}

function TimeSliderVisibility({ onTimeChange, forecastValue }) {
  // Inicializa el slider en 0 y ajusta la fecha inicial
  const [sliderValue, setSliderValue] = useState(0);
  const [adjustedDateTime, setAdjustedDateTime] = useState(formatDateForDisplay(new Date(forecastValue)));
  const [isAutoPlay, setIsAutoPlay] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

  // Monitorea el ancho de la ventana para ajustes responsivos
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Cuando forecastValue cambia, reinicia el slider a 0 y ajusta la fecha
    setSliderValue(0);
    const initialDate = new Date(forecastValue);
    setAdjustedDateTime(formatDateForDisplay(initialDate));

    const geoServerDateTime = formatDateForGeoServer(initialDate);
    onTimeChange(geoServerDateTime);
  }, [forecastValue]);

  const handleSliderChange = (event, value) => {
    setSliderValue(value);
    const baseDate = new Date(forecastValue);
    baseDate.setUTCMinutes(baseDate.getUTCMinutes() + value * 30); // Incrementa 30 minutos por paso en UTC
    const geoServerDateTime = formatDateForGeoServer(baseDate);
    const displayDateTime = formatDateForDisplay(baseDate);
    setAdjustedDateTime(displayDateTime);
    onTimeChange(geoServerDateTime); // Envía el tiempo al componente principal
  };

  const handlePlayButtonClick = () => {
    setIsAutoPlay(!isAutoPlay);
  };

  useEffect(() => {
    if (isAutoPlay) {
      const interval = setInterval(() => {
        setSliderValue((prevValue) => {
          const nextValue = prevValue < 96 ? prevValue + 1 : 0;
          handleSliderChange(null, nextValue);
          return nextValue;
        });
      }, 1300);
      return () => clearInterval(interval);
    }
  }, [isAutoPlay, forecastValue]);

  // RESPONSIVIDAD
  // Determina el ancho del slider según el tamaño de pantalla
  const getSliderWidth = useMemo(() => {
    switch (true) {
      case windowWidth <= 275:
        return '50%';
      case windowWidth <= 500:
        return '70%';
      case windowWidth <= 700:
        return '77%';
      case windowWidth <= 900:
        return '84%';
      case windowWidth <= 1100:
        return '83%';
      case windowWidth <= 1500:
        return '88%';
      default:
        return '90%';
    }
  }, [windowWidth]);

  // Para la posición vertical de la barra del slider
  const getSliderTop = () => {
    if (windowWidth <= 700) return '61%';
    if (windowWidth <= 900) return '58%';
    if (windowWidth <= 1100) return '60%';
    if (windowWidth <= 1200) return '59%';
    if (windowWidth <= 1500) return '52%';
    return '50%';
  };

  // Tamaño de la fuente para el valor del label según el ancho de la pantalla
  const getValueLabelFontSize = useMemo(() => {
    switch (true) {
      case windowWidth <= 275:
        return '0.65rem';
      case windowWidth <= 500:
        return '0.7rem';
      case windowWidth <= 768:
        return '0.75rem';
      default:
        return '0.875rem';
    }
  }, [windowWidth]);

  // Padding para el label del valor según el ancho de la pantalla
  const getValueLabelPadding = useMemo(() => {
    switch (true) {
      case windowWidth <= 275:
        return '0.25rem 0.5rem';
      case windowWidth <= 500:
        return '0.3rem 0.6rem';
      case windowWidth <= 768:
        return '0.4rem 0.8rem';
      default:
        return '0.5rem 1rem';
    }
  }, [windowWidth]);

  // Tamaño del ícono del botón (play/stop) según el ancho de la pantalla
  const getIconSize = useMemo(() => {
    switch (true) {
      case windowWidth <= 275:
        return 20;
      case windowWidth <= 500:
        return 22;
      case windowWidth <= 768:
        return 24;
      default:
        return 28;
    }
  }, [windowWidth]);

  // Define la cercanía entre los marks y su label
  const getMarkTop = useMemo(() => {
    switch (true) {
      case windowWidth <= 275:
        return '57%';
      case windowWidth <= 320:
        return '60%';
      case windowWidth <= 350:
        return '60%';
      case windowWidth <= 750:
        return '59%';
      case windowWidth <= 1000:
        return '59%';
      case windowWidth <= 1100:
        return '58%';
      case windowWidth <= 1200:
        return '59%';
      case windowWidth <= 1300:
        return '52%';
      case windowWidth <= 1500:
        return '52%';
      case windowWidth <= 1800:
        return '50%';
      default:
        return '50%';
    }
  }, [windowWidth]);

  const getMarkLabelTop = useMemo(() => {
    // Se utiliza el mismo valor para todas las condiciones para mantener la consistencia
    switch (true) {
      case windowWidth <= 275:
        return '33.5px';
      case windowWidth <= 320:
        return '33.5px';
      case windowWidth <= 350:
        return '33.5px';
      case windowWidth <= 750:
        return '33.5px';
      case windowWidth <= 1000:
        return '31px';
      case windowWidth <= 1100:
        return '30.5px';
      case windowWidth <= 1200:
        return '30.55px';
      case windowWidth <= 1300:
        return '-7.5px';
      case windowWidth <= 1500:
        return '-7.5px';
      case windowWidth <= 1800:
        return '-7.5px';
      default:
        return '-7.5px';
    }
  }, [windowWidth]);

  const marks = useMemo(() => {
    let stepInterval;
    if (windowWidth < 500) {
      stepInterval = 16; // cada 8 horas (16 * 30 minutos = 480 minutos)
    } else if (windowWidth < 750) {
      stepInterval = 8; // cada 4 horas (8 * 30 = 240 minutos)
    } else if (windowWidth < 1000) {
      stepInterval = 8; // cada 2 horas (4 * 30 = 120 minutos)
    } else {
      stepInterval = 6; // cada 1 hora (2 * 30 = 60 minutos)
    }
    return Array.from({ length: 97 }, (_, index) =>
      index % stepInterval === 0 ? { value: index, label: `+${(index * 0.5).toFixed(0)}` } : null
    ).filter(Boolean);
  }, [windowWidth]);


  return (
    <div className="time-slider-container">
      {/* Play/Stop Button */}
      <button
        className={`ts-custom-btn ${isAutoPlay ? 'ts-custom-btn--stop' : 'ts-custom-btn--play'}`}
        onClick={handlePlayButtonClick}
        aria-label={isAutoPlay ? "Stop autoplay" : "Start autoplay"}
      >
        {isAutoPlay ? (
          <StopCircleRoundedIcon />
        ) : (
          <PlayCircleOutlineRoundedIcon />
        )}
      </button>
      <MuiSlider
        className="time-slider-horizontal"
        min={0}
        max={96} // 96 pasos de 30 minutos (48 horas)
        value={sliderValue}
        step={1}
        onChange={handleSliderChange}
        valueLabelDisplay="on"
        valueLabelOpen
        valueLabelFormat={(value) => {
          const baseDate = new Date(forecastValue);
          baseDate.setUTCMinutes(baseDate.getUTCMinutes() + value * 30);
          return formatDateForDisplay(baseDate);
        }}
        marks={marks}
        sx={{
          // Only essential overrides that can't be handled by CSS
          '& .MuiSlider-valueLabel': {
            fontSize: windowWidth <= 350 ? '0.6rem' :
              windowWidth <= 500 ? '0.65rem' :
                windowWidth <= 768 ? '0.7rem' : '0.75rem',
          },
          '& .MuiSlider-markLabel': {
            fontSize: windowWidth <= 350 ? '0.6rem' :
              windowWidth <= 500 ? '0.65rem' :
                windowWidth <= 768 ? '0.7rem' : '0.75rem',
          }
        }}
      />
    </div>
  );
}

export default TimeSliderVisibility;
